import { HStack, Text, VStack } from '@chakra-ui/react'
import { WorkflowsContext } from '@context'
import { Form, Formik } from 'formik'
import React, { useContext } from 'react'
import { useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { EditDrawerFooter } from '../../../edit-drawer/edit-drawer-footer'
import { FilterFieldArray } from '../shared-filters/filter-field-array'
import { ALL_WORKFLOW_FILTERS } from '../shared-filters/lib/constants'
import type { FilterCatalogMap } from '../shared-filters/lib/types'
import {
  SESSIONS_COMPLETED_OPERATOR_SCHEMA,
  SESSIONS_COMPLETED_VALUES_SCHEMA
} from '../shared-filters/sessions-completed-filter'

type RouteParams = {
  workflowId: string
  organizationId: string
}

type Props = {
  closeDrawer: () => void
  onSubmit: (trigger: Trigger) => void
}

type Trigger = {
  workflowId: string
  triggerType: string
  config: TriggerConfig
}

type TriggerConfig = {
  filters?: Array<{
    filterType: string
    filterOperator: string
    filterValues: Array<number>
  }>
}

const filterCatalog: FilterCatalogMap = {
  sessionsCompleted: ALL_WORKFLOW_FILTERS.sessionsCompleted
}

const schema = Yup.object().shape({
  sessionsCompletedOperator: SESSIONS_COMPLETED_OPERATOR_SCHEMA,
  sessionsCompletedValues: SESSIONS_COMPLETED_VALUES_SCHEMA
})

const determineFilterConfig = (
  filterType: string,
  values: {
    sessionsCompletedOperator: string
    sessionsCompletedValues: Array<number>
    sessionsCompletedUIValue: string
  }
) => {
  switch (filterType) {
    case 'sessionsCompleted':
      if (values.sessionsCompletedUIValue === 'firstSession') {
        return {
          filterType,
          filterOperator: values.sessionsCompletedOperator,
          filterValues: values.sessionsCompletedValues
        }
      }
      return null
    default:
      return null
  }
}

const prepareFiltersForSubmission = (values: {
  sessionsCompletedOperator: string
  sessionsCompletedValues: Array<number>
  sessionsCompletedUIValue: string
  filterTypes: Array<string>
}) => {
  if (values.sessionsCompletedUIValue === 'everySession') {
    return []
  }

  return values.filterTypes
    .map((type: string) => determineFilterConfig(type, values))
    .filter(config => config !== null) as Array<{
    filterType: string
    filterOperator: string
    filterValues: number[]
  }>
}

const getUIValueFromFilter = (
  filters:
    | Array<{
        filterType: string
        filterValues: Array<number>
        filterOperator: string
      }>
    | undefined
) => {
  const sessionsFilter = filters?.find(
    f => f.filterType === 'sessionsCompleted'
  )

  if (!sessionsFilter) {
    return 'everySession'
  }

  if (
    sessionsFilter.filterOperator === 'equal' &&
    sessionsFilter.filterValues.length === 1 &&
    sessionsFilter.filterValues[0] === 1
  ) {
    return 'firstSession'
  }

  return 'everySession'
}

export const SessionCompletedTrigger = ({ closeDrawer, onSubmit }: Props) => {
  const { selectedNode } = useContext(WorkflowsContext)
  const { workflowId } = useParams<RouteParams>()
  const config = selectedNode?.config
  const filters = config?.filters

  const uiValue = getUIValueFromFilter(filters)

  const initialValues = {
    sessionsCompletedOperator:
      filters?.find(
        (f: {
          filterType: string
          filterValues: Array<number>
          filterOperator: string
        }) => f.filterType === 'sessionsCompleted'
      )?.filterOperator || (uiValue === 'firstSession' ? 'equal' : ''),
    sessionsCompletedValues:
      filters?.find(
        (f: {
          filterType: string
          filterValues: Array<number>
          filterOperator: string
        }) => f.filterType === 'sessionsCompleted'
      )?.filterValues || (uiValue === 'firstSession' ? [1] : []),
    sessionsCompletedUIValue: uiValue,
    // Ensure filterTypes includes 'sessionsCompleted' when we have a sessions filter
    filterTypes: filters?.length
      ? filters.some(
          (f: { filterType: string }) => f.filterType === 'sessionsCompleted'
        )
        ? ['sessionsCompleted']
        : filters.map((f: { filterType: string }) => f.filterType)
      : uiValue === 'firstSession'
        ? ['sessionsCompleted']
        : []
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={values => {
        const trigger: Trigger = {
          workflowId,
          triggerType: 'session-completed-trigger',
          config: {
            filters: prepareFiltersForSubmission(values)
          }
        }
        onSubmit(trigger)
      }}
    >
      {({ values, errors, resetForm: resetFields }) => {
        const resetForm = (updates: {
          sessionsCompletedOperator: string
          sessionsCompletedValues: Array<string>
          filterTypes: Array<string>
        }) => {
          const updatedValues = { ...initialValues, ...updates }
          resetFields({ values: updatedValues })
        }

        return (
          <Form>
            <VStack
              id="vstack-session-completed"
              align="flex-start"
              spacing="xsmall"
              w="100%"
            >
              <HStack
                id="hstack-session-completed"
                w="100%"
                spacing="small"
                justify="space-between"
              />
              <Text as="b">Filters</Text>
              <FilterFieldArray
                values={values}
                filterCatalog={filterCatalog}
                resetForm={resetForm}
              />
            </VStack>
            <EditDrawerFooter onCancel={closeDrawer} hideDelete />
          </Form>
        )
      }}
    </Formik>
  )
}
